import _, { cloneDeep } from 'lodash'
import HttpStatus from 'http-status-codes'
import { config } from '../components/Init/Init'
import l10n from '../components/i18n/I18N'
import moment from 'moment'
import { floor } from 'mathjs'
import 'moment/locale/es'
import 'moment/locale/pt'
import 'moment/locale/de'
import 'moment/locale/fi'
import 'moment/locale/tr'
import 'moment/locale/ru'
import { isAndroid, isMobile, isIOS, osVersion } from 'react-device-detect'
import { getPlayerStatusFromStatus } from './helpers/playerHelper'
import { getPlayerStatus } from './helpers/playerHelper'
import { playerProductsStoreKeys } from './store/playerStore/playerStoreConstants'
import { openCXMessenger, closeCXMessenger, isCXmessengerAdded } from '../components/Help/CXMessenger'
import { getStorePriceDisplayString } from '../sweepstakes/SweepsClaimCenter/sweepsClaimCenterUtils'
import { closeZendeskWidget, isZendeskWidgetAdded, openZendeskWidget } from '../components/Help/ZendeskWidget'
import { readZustandLocalStorage } from './store/localStorageUtils'
import { playerStoreKey } from '../services/store/playerStore/playerStoreConstants'
import { isNullOrUndefined } from './helpers'
import { closeLiveChatWidget, isLiveChatWidgetAdded, openLiveChatWidget } from '../components/Help/LiveChat'

/**
 * @name tokenizer
 *
 * @param {string} str - A string containing tokens to replace.
 * @param {object} tokens - An object of tokens and their replacements where
 *  the key is the token and the value is the replacement.
 *
 * @desc A filter that replaces tokens with the specified text. Useful for RESTful
 *  urls and localization.
 *
 */
const tokenizer = function(str, tokens) {
  _.forEach(_.toPairs(tokens), function(token) {
    if (typeof str === 'string') {
      str = str.replace(new RegExp(token[0], 'g'), token[1])
    } else {
      str = ''
    }
  })

  return str
}

const toCents = function(amount) {
  return Math.trunc(amount * config.centsMultiplier)
}

const convertToDollar = function(totals, locale, currencyCode) {
  let totalFinal
  let totalConverted
  let cents
  let negativeSign
  if (Math.sign(totals) !== -1) {
    if (totals.toString().length >= 2) {
      totalConverted = totals.toString().slice(0, -2)
      cents = '.' + totals.toString().slice(-2)
      totalFinal = formatValue(locale, currencyCode, 2, '', totalConverted, cents)
    } else {
      totalConverted = totals.toString().slice(0, -1)
      cents = '0.0' + totals.toString().slice(-1)
      totalFinal = formatValue(locale, currencyCode, 0, '', totalConverted, cents)
    }
    return totalFinal
  } else {
    negativeSign = totals.toString().slice(0, 1)
    if (totals.toString().length > 2) {
      totalConverted = totals.toString().slice(1, -2)
      cents = '.' + totals.toString().slice(-2)
      totalFinal = formatValue(locale, currencyCode, 2, negativeSign, totalConverted, cents)
    } else {
      cents = '0.0' + totals.toString().slice(-1)
      totalFinal = formatValue(locale, currencyCode, 0, negativeSign, '', cents)
    }
    return totalFinal
  }
}

const localizedTruncate = function(number, isTruncated) {
  if (isNullOrUndefined(number)) {
    number = 0
  }

  if (typeof number === 'string') {
    // Unfortunately we can't rely on parseInt to always give us a number
    // '1,000' will return 1 instead of NaN for instance. Especially since
    // we're using non-US numbers, ',' and '.' are interchangeable and
    // shouldn't be assumed to be one or the other.
    // If we encounter any character not in the validChars below, we treat
    // this as a string and return the "number" back.
    const validChars = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', ',']
    number.split('').forEach(function(chr) {
      if (!validChars.includes(chr)) return number
    })
    number = parseInt(number, 10)
  }

  if (isTruncated && number >= config.oneMillion) {
    const n = Math.floor(number).toString()
    let numString

    if (number >= config.oneBillion) {
      numString = n.slice(0, n.length - 8) + '.' + n.slice(n.length - 8, n.length - 7)
    } else if (number >= config.oneMillion) {
      numString = n.slice(0, n.length - 5) + '.' + n.slice(n.length - 5, n.length - 4)
    }

    const numRounded = (Math.round(parseFloat(numString)) / 10).toFixed(1)
    const numLocal = numRounded.toLocaleString()

    if (number >= config.oneBillion) {
      return numLocal + l10n.t['account.abbreviationBillions']
    } else if (number >= config.oneMillion) {
      return numLocal + l10n.t['account.abbreviationMillions']
    }
  } else {
    return number.toLocaleString()
  }
}

// compare function for tout builder, used with .find()s or .findIndex()s looping through toutArrays
// tests if given id exists within an object at its first breakpoint
const equalsFirstKey = (currentObject, idToTest) => {
  if (currentObject.slot[Object.keys(currentObject.slot)[0]].id === idToTest) {
    return true
  } else {
    return false
  }
}

function formatValue(locale, currencyCode, num, mathSign, value1, value2) {
  return new Intl.NumberFormat(l10n.browserDateLocale(), {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: num
  }).format(mathSign + value1 + value2)
}

const humanReadableTime = function(time, language) {
  // set locale for humanized times
  moment.locale(language)

  return typeof time === 'number' ? moment.duration(time, 'minutes').humanize() : time
}

/**
 * reUpAuthToken
 *
 * @param {object} expired - The player object with the expired user token
 *
 * @desc
 *  A function that sets a new user auth token when the supplied interval
 *  elapses.
 *
 */
const reUpAuthToken = function(expired) {
  // request new token
  if (_.has(expired, 'loginUser')) {
    return fetch(config.apiUrl + config.authUrl, {
      method: 'POST',
      mode: 'cors',
      body: JSON.stringify({
        platform: 1,
        localeTag: l10n.langCode,
        loginType: config.reUpType,
        identifier: expired.loginUser.email,
        context: expired.refreshToken.encryptedToken,
        timezoneOffset: config.timezone,
        analyticsData: {
          browser: window.navigator.appVersion,
          userIp: '1.1.1.1',
          playVersion: config.version,
          clientApplicationId: config.clientApplicationId,
          device: 'browser',
          applicationId: config.applicationId,
          originId: config.originId
        }
      }),
      headers: {
        'Content-Type': 'application/json',
        ApplicationToken: config.appToken
      }
    }).catch(err => {
      console.log('[reUpAuthToken] - ERROR!', err)
    })
  } else {
    return expired
  }
}

const sliceTitle = title => {
  /**
   * sliceTitle
   *
   * @param {string} title      - The game title to be sliced.
   */

  // TODO: set title based on words not characters
  let words

  if (title.length > config.sliceTitleLength) {
    words =
      title
        .split('')
        .slice(0, title.lastIndexOf(' ', config.sliceTitleIndexOf))
        .join('') + '...'
  } else {
    words = title
  }

  return words
}

const getAllowDemoGames = status => {
  const allowDemoGamesWhileLoggedIn = _.defaultTo(config.allowDemoGamesWhileLoggedIn, true)
  if (!allowDemoGamesWhileLoggedIn && getPlayerStatusFromStatus(status) === 'logged-in') {
    return false
  }
  return true
}

const getShowPlayButtons = status => {
  if (isMobile) {
    const mobileShowPlayButtonIfSolo = _.defaultTo(config.mobileShowPlayButtonIfSolo, true)
    if (!mobileShowPlayButtonIfSolo && !getAllowDemoGames(status)) {
      return false
    }
  }
  return true
}

const getUseRoundPlayButton = status => {
  const useRoundPlayButtonIfSolo = _.defaultTo(config.useRoundPlayButtonIfSolo, false)
  if (useRoundPlayButtonIfSolo && !getAllowDemoGames(status)) {
    return true
  }
  return false
}

const gameIsDemoEnabled = gameId => {
  return config.demoGameIds && config.demoGameIds.indexOf(gameId) > -1
}

const demoGamesAvailable = () => {
  return config.demoGameIds && config.demoGameIds.length > 0
}

const getWallet = (wallets, walletId) => {
  if (wallets === undefined || wallets === null) {
    return 0
  }
  const wallet = wallets[walletId]
  return wallet ? parseFloat(wallet.amount) : 0
}

const getPageTitle = pageId => {
  let title = l10n.t['page.' + pageId + '.title']
  if (config.enableSweepstakes) {
    title = l10n.t['page.sweeps.title']
  } else {
    if (!title || title.length === 0) {
      // fallback to site title if unset
      title = process.env.REACT_APP_SITE_TITLE
    }
  }
  return title
}

const getPageDesc = pageId => {
  return l10n.t['page.' + pageId + '.description']
}

const convertUrlToWebp = url => {
  let newUrl = _.replace(url, '.jpg', '.webp')
  newUrl = _.replace(newUrl, '.jpeg', '.webp')
  newUrl = _.replace(newUrl, '.png', '.webp')
  newUrl = _.replace(newUrl, '.svg', '.webp')
  return newUrl
}

const convertUrlToJpg = url => {
  let newUrl = _.replace(url, '.webp', '.jpg')
  newUrl = _.replace(newUrl, '.jpeg', '.jpg')
  newUrl = _.replace(newUrl, '.png', '.jpg')
  newUrl = _.replace(newUrl, '.svg', '.jpg')
  return newUrl
}

const getImageMimeType = url => {
  if (!url) {
    return ''
  }
  if (url.indexOf('.jpg') > -1 || url.indexOf('.jpeg') > -1) {
    return 'image/jpeg'
  }
  if (url.indexOf('.png') > -1) {
    return 'image/png'
  }
  if (url.indexOf('.webp') > -1) {
    return 'image/webp'
  }
  if (url.indexOf('.svg') > -1) {
    return 'image/svg+xml'
  }
}

/**
 * @name getFeatureByLocale
 * @description Handles the feature by locale and default locale
 * @param {string} Locale - The locale eg. "en-row", "en-us"
 * @param {string} feature - The feature info requested eg. "paymentMethods", "lowBalance"
 *
 * @return {object} - The locale feature or default locale feature
 */
const getFeatureByLocale = (locale, feature) => {
  let localeFeature = _.get(config.featuresByLocale, `${locale}`)
  const { defaultLocale } = config.featuresByLocale

  // If the locale is not specified on i18n-cfg.json we load the defaultLocale
  if (!localeFeature) {
    localeFeature = _.get(config.featuresByLocale, defaultLocale)
  }

  let featureData = _.get(localeFeature, `${feature}`)

  // If the feature is not defined we load default
  if (!featureData) {
    featureData = _.get(config.featuresByLocale, `${defaultLocale}.${feature}`)
  }

  return featureData
}

/**
 * @name getFeatureByLocaleMerged
 * @description Handles the feature by locale merged with default
 * @param {string} Locale
 * @param {string} feature
 *
 * @return {object} - The locale feature merged with default locale feature
 */
const getFeatureByLocaleMerged = (locale, feature) => {
  let localeFeature = _.get(config.featuresByLocale, `${locale}`)
  const { defaultLocale } = config.featuresByLocale

  // If the locale is not specified on i18n-cfg.json we load the defaultLocale
  if (!localeFeature) {
    localeFeature = _.get(config.featuresByLocale, defaultLocale)
  }

  let featureData = _.get(localeFeature, `${feature}`)

  if (featureData) {
    const defaultData = (featureData = _.get(config.featuresByLocale, `${defaultLocale}.${feature}`))
    const result = _.merge(defaultData, featureData)

    return result
  } else {
    featureData = _.get(config.featuresByLocale, `${defaultLocale}.${feature}`)
  }

  return featureData
}

/**
 * @name playerPermissions
 * @description Handle player permissions
 * @param {object} playerData
 *
 * @return {object}
 */
const playerPermissions = playerData => {
  const permissions = {
    kycVerified: true
  }

  if (playerData.status === config.status.registered) {
    const iovation =
      playerData.iovation === 'review' &&
      (playerData.kycStatus === config.kycStatuses.NotVerified ||
        playerData.kycStatus === config.kycStatuses.Expired) &&
      config.enableIovation

    if (config.enableKyc && (iovation || _.get(playerData.kycInfo, 'requested'))) {
      permissions.kycVerified = false
    }
  }

  return permissions
}

// TODO: please document this function
const elementSupportsAttribute = (element, attribute) => {
  var test = document.createElement(element)
  if (attribute in test) {
    return true
  } else {
    return false
  }
}

/**
 * @name makeCMSSiteName
 *
 * @param {string} casino - The casino name.
 * @param {string} cmsPrefixOverride - The CMS prefix, if we should override (instead of casino name).
 * @param {string} locale - The ISO locale the casino is operating in.
 *
 * @desc A function that generates the CMS site name used for GraphQL queries.
 */
const makeCMSSiteName = function(casino, cmsPrefixOverride, locale) {
  return `${cmsPrefixOverride || casino}_${_.replace(locale, /-/g, '_')}`
}

/**
 * @desc Get an object containing wallets with currencies that may be redeemed
 * @param {object} wallets - Set of wallets to filter for redeem
 * @returns {object} - Set of wallets that can be used to redeem
 */
const getRedeemablePlayerWallets = wallets => {
  if (config.cryptoRedeemableWalletIds) {
    let validWallets = {}
    config.cryptoRedeemableWalletIds.forEach(walletId => {
      if (wallets[walletId]) {
        validWallets[walletId] = cloneDeep(wallets[walletId])
      }
    })

    return validWallets
  }

  return wallets
}

/**
 * @desc Formats the currency amount based on config.
 * @param {number} amount - The wallet amount.
 * @param {object} currencyConfig - The wallet currency config.
 * @returns {string} - The truncated currency string.
 */
const formatCurrencyAmountFromConfig = (amount, currencyConfig) => {
  if (isNullOrUndefined(amount)) {
    amount = 0
  }

  if (!currencyConfig) {
    return amount
  }

  if (typeof amount === 'string') {
    const numberAmount = Number(amount.trim())

    amount = isNaN(numberAmount) ? 0 : numberAmount
  }

  if (currencyConfig.callLocalizedTruncate) {
    // call the default localizedTruncate method if this is set
    return localizedTruncate(amount, false)
  }

  let autoFillDecimals = currencyConfig.autoFillDecimals
  if (currencyConfig.autoFillDecimalsIfNonInteger) {
    if (Math.round(amount) !== amount) {
      autoFillDecimals = true
    }
  }
  let maxDecimals = currencyConfig.maxDecimals

  if (currencyConfig.hasOwnProperty('autoDivide')) {
    amount /= currencyConfig.autoDivide
  }

  if (currencyConfig.hasOwnProperty('decimalsThreshold')) {
    if (amount >= currencyConfig.decimalsThreshold) {
      autoFillDecimals = false
      maxDecimals = 0
    }
  }

  if (currencyConfig.alwaysRoundDown) {
    if (maxDecimals === 0) {
      // the Math.js floor method isn't working correctly for large numbers, just use Math.floor if there's nothing unique
      amount = Math.floor(amount)
    } else {
      amount = floor(amount, maxDecimals)
    }
  }

  if (Number.isInteger(amount)) {
    if (autoFillDecimals) {
      if (currencyConfig.localize) {
        return amount.toLocaleString(undefined, {
          minimumFractionDigits: maxDecimals,
          maximumFractionDigits: maxDecimals
        })
      } else {
        return amount.toFixed(maxDecimals)
      }
    } else {
      if (currencyConfig.localize) {
        return amount.toLocaleString()
      } else {
        return amount
      }
    }
  } else {
    if (currencyConfig.localize) {
      if (autoFillDecimals) {
        return amount.toLocaleString(undefined, {
          minimumFractionDigits: maxDecimals,
          maximumFractionDigits: maxDecimals
        })
      } else {
        return amount.toLocaleString(undefined, {
          maximumFractionDigits: maxDecimals
        })
      }
    } else {
      const amountString = amount.toString()
      const number = amountString.slice(0, amountString.indexOf('.') + 1)
      const decimals = amountString.slice(amountString.indexOf('.') + 1, amountString.length)

      if (decimals.length > maxDecimals) {
        return number + decimals.slice(0, maxDecimals)
      } else {
        if (autoFillDecimals) {
          return number + decimals + '0'.repeat(maxDecimals - decimals.length)
        } else {
          return amountString
        }
      }
    }
  }
}

/**
 * @desc Formats the currency amount based on config.
 * @param {number} amount - The wallet amount.
 * @param {string} currencyId - The wallet currency ID.
 * @returns {string} - The truncated currency string.
 */
const formatCurrencyAmount = (amount, currencyId) => {
  let mainConfig = getCurrencyConfigForCurrencyId(currencyId)
  const currencyConfig = mainConfig ? mainConfig : _.find(config.additionalCurrencyParsing, { currencyId })
  return formatCurrencyAmountFromConfig(amount, currencyConfig)
}

/**
 * @desc Check if img loading attribute is supported
 * @returns {boolean} - if the attribute is supported
 */
const isImgLoadingAttributeSupported = () => {
  const version16 = 16
  const isIOSSmallerThan16 = isIOS && !isNaN(parseFloat(osVersion)) && parseFloat(osVersion) < version16

  if (elementSupportsAttribute('img', 'loading') && !isIOSSmallerThan16) {
    return true
  }
  return false
}

/**
 * @desc Checks if category have custom template
 * @returns {boolean} - does category have custom template
 */
const isCustomCategory = template => {
  return !config.regularCategoriesTemplates.includes(template)
}

/**
 * @desc Make sure freeze verification occurs prior to opening any requests for those specific features
 * Currently used for purchases, redemptions, and game loads
 * @param {object} playerFreezeInfo - The player freeze data from firebase.
 * @param {object} type - Freeze type.
 * @returns {boolean} - does player have freeze state
 */
const verifyNotFrozen = (playerFreezeInfo, type) => {
  // If no type specified, default to "allFrozen"
  if (type == null) {
    type = config.freezeEvents.allFrozen
  }

  // Check freeze status in Firebase
  if (playerFreezeInfo && playerFreezeInfo[type]) {
    return false
  }

  // Nothing says we're frozen, huzzah!
  return true
}

/**
 * @desc Checks to see if game id, provider, and/or secondary provider is restricted for testing.
 * The config for this:
 * The form is - "providersRestrictedToTestAccounts": {"ProviderName":["Secondary Provider"]}
 * The secondary provider is specific to providers that are game aggregators.
 * If the provider is not an aggregator, then leave array empty.
 * Example to restrict games by id: "gameIdsRestrictedToTestAccounts":[1010, 1230, 13004].
 * Example to restrict provider: { "vivo": ["", "redrake"], "pragmatic": [] }.
 * This would restrict all pragmatic games, vivo live table games, and vivo games that have redrake as a secondary provider.
 * @returns {boolean} - true if game id, provider, and/or secondary provider are restricted for testing.
 */
const isGameRestrictedForTesting = (gameId, provider, secondaryProvider) => {
  // Is this game id restricted to test accounts
  if (config.gameIdsRestrictedToTestAccounts.indexOf(gameId) > -1) {
    return true
  }

  let restrictedObj = config.providersRestrictedToTestAccounts
  if (restrictedObj[provider]) {
    // Does game provider have secondary provider restrictions?
    if (restrictedObj[provider].length > 0) {
      return restrictedObj[provider].indexOf(secondaryProvider) > -1
    } else {
      // No secondary provider defined, restrict all
      return true
    }
  }

  return false
}

const isStatusNotification = notification => {
  return notification.type === config.popupType.depositStatus || notification.type === config.popupType.withdrawalStatus
}

const isNotificationLimit = notification => {
  return notification.type === config.popupType.limitReached
}

const isNotificationExpired = notification => {
  if (notification && notification.context && notification.context.popupEndDate) {
    let expirationDate = new Date(notification.context.popupEndDate)
    let currentDate = new Date()
    return currentDate > expirationDate
  } else {
    return false
  }
}

const isNotificationTimePending = notification => {
  if (notification && notification.context && notification.context.popupStartDate) {
    const startDate = new Date(notification.context.popupStartDate)
    const currentDate = new Date()
    return currentDate < startDate
  } else {
    return false
  }
}

const isNotificationPurchaseType = notification => {
  if (
    notification &&
    notification.context &&
    (notification.context.directPurchaseUUID || notification.context.directPurchaseUUIDs)
  ) {
    return true
  }
  return false
}

const getPurchaseNotificationUuids = notification => {
  if (isNotificationPurchaseType(notification)) {
    if (notification.context.directPurchaseUUID) {
      return [notification.context.directPurchaseUUID]
    } else {
      return notification.context.directPurchaseUUIDs.split(',')
    }
  }
  return null
}

const arePurchaseNotificationProductsAvailable = (playerProductsStore, notification) => {
  const uuids = getPurchaseNotificationUuids(notification)
  if (uuids) {
    let storeHasProducts = true
    _.forEach(uuids, uuid => {
      if (!storeHasProduct(playerProductsStore, uuid)) {
        storeHasProducts = false
        return false
      }
    })
    return storeHasProducts
  }
  // hopefully we're not calling it otherwise, but just in case...
  return true
}

const getPurchaseNotificationUuidForCtaNumber = (notification, ctaNumber = undefined) => {
  let uuid = null

  const uuids = getPurchaseNotificationUuids(notification)
  if (uuids && uuids.length > 0) {
    let index

    let ctaNumberInteger = parseInt(ctaNumber, 10)
    if (isNaN(ctaNumberInteger) || ctaNumberInteger < 1) {
      index = 0
    } else {
      // ctaNumber is 1-indexed, arrays are 0-indexed; convert
      index = ctaNumberInteger - 1
    }

    if (uuids.length > index) {
      uuid = uuids[index]
    }
  }

  return uuid
}

const getNotificationCtaForCtaNumber = (notification, ctaNumber = undefined) => {
  let cta = null

  if (notification && notification.context) {
    const ctaNumberInteger = parseInt(ctaNumber, 10)
    if (isNaN(ctaNumberInteger) || ctaNumberInteger < 1) {
      // if our ctaNumber isn't an integer 1+, then we'll check base cta first and fallback to cta1
      if (notification.context.cta) {
        cta = notification.context.cta
      } else if (notification.context.cta1) {
        cta = notification.context.cta1
      }
    } else {
      // otherwise, we'll check the appropriate ctaX first and fallback to base cta
      if (notification.context.hasOwnProperty('cta' + ctaNumberInteger)) {
        cta = notification.context['cta' + ctaNumberInteger]
      } else if (notification.context.cta) {
        cta = notification.context.cta
      }
    }
  }

  return cta
}

/**
 * @desc Return the timezone offset as a string (like '-04:00' as we see on the social login)
 * @returns {string}
 */
const getTimezoneOffsetString = () => {
  // int value for timezone
  let offset = new Date().getTimezoneOffset()
  if (offset !== 0) {
    offset = -offset
  }

  // convert to desired string format
  let sign = '+'
  if (offset < 0) {
    sign = '-'
    offset = -offset
  }
  let hours = Math.floor(offset / 60)
  let minutes = Math.floor(offset % 60)
  let output = sign + ('0' + hours).slice(-2) + ':' + ('0' + minutes).slice(-2)

  // return formatted timezone string
  return output
}

/**
 * @desc Return the timezone offset as a string (like '-04:00' as we see on the social login).
 *       If the player social data has a timezone offset, use that, otherwise use the timezone from JS.
 * @returns {string}
 */
const getTimezoneOffsetStringFromSocialData = socialData => {
  if (socialData && socialData.timezoneOffset && socialData.timezoneOffset.length > 0) {
    return socialData.timezoneOffset
  } else {
    return getTimezoneOffsetString()
  }
}

/**
 * @desc Convert the claim center nextTriggerTime to a ms timestamp, converting to local timezone if applicable
 * @returns {number}
 */
const convertNextTriggerTimeToMs = (nextTriggerTime, socialData) => {
  let date
  if (nextTriggerTime.indexOf('Z') > 0) {
    // utc timezone, use string as is
    date = new Date(nextTriggerTime)
  } else {
    // otherwise, append local timezone
    date = new Date(nextTriggerTime + getTimezoneOffsetStringFromSocialData(socialData))
  }
  return date.getTime()
}

/**
 * @desc Return launch path for game.
 * @param {string} gameId - numeric id for game
 * @param {string} mode - Values for mode are 'F' for free and 'R' for real. 'H' for history is not implemented at this time.
 * @returns {string}
 */
const getPathToGame = (gameId, mode) => {
  return `/${l10n.locale}/game/${mode}/${gameId}`
}

/**
 * @desc Given the location (from useLocation or window.location), checks if the current path is in a game
 * @returns {boolean} - true if we're in a game
 */
const isInGame = location => {
  return _.includes(location.pathname, 'game/R') || _.includes(location.pathname, 'game/F')
}

/**
 * @desc Given the location (from useLocation), checks if the current path is in the lobby.
 *       Any lobby location (including promotions page, for example) should set this true.
 * @returns {boolean} - true if we're in the lobby
 */
const isInLobby = location => {
  return _.includes(location.pathname, '/casino/lobby')
}

/**
 * @desc Check if the player is a test user, according to their login 'testUser' status
 * @returns {boolean} - true if the player is a test user
 */
const isPlayerTestAccount = playerData => {
  return playerData && getPlayerStatus(playerData) !== 'logged-out' && playerData.testUser === true
}

/**
 * @desc Check if we can bypass maintenance, according to bypass param and account test user status.
 * @returns {boolean} - true if we can bypass maintenance
 */
const canBypassMaintenance = (bypassMaintenanceParam, playerData, checkForTestUser = true) => {
  if (bypassMaintenanceParam === config.bypassMaintenanceParam) {
    if (checkForTestUser) {
      // make sure the player is a test user, otherwise they still get maintenance mode
      if (isPlayerTestAccount(playerData)) {
        return true
      }
    } else {
      // if we're not checking the test user status, then this is enough to bypass
      return true
    }
  }
  return false
}

/**
 * @desc Retrieves gdms game data by a game id.
 * @param {string} gameId - numeric id for game
 * @param {Function} successCallback - callback function if succeeds fails
 * @param {Function} failCallback - callback function if retrieval fails
 */
const getGameObj = (gameId, successCallback, failCallback) => {
  fetch(
    tokenizer(config.apiUrl + config.gameObjectUrl, {
      '{ugid}': gameId
    }),
    {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        ApplicationToken: config.appToken
      }
    }
  )
    .then(response => {
      if (response.status === HttpStatus.OK) {
        response.json().then(gameObj => {
          successCallback(gameObj)
        })
      } else {
        response.json().then(err => {
          failCallback(err)
        })
      }
    })
    .catch(err => {
      failCallback(err)
    })
}

// trying to account for some mistakes that might be made in the CMS...
const cleanUpEntryTags = entryTags => {
  if (entryTags) {
    // clear any start/end whitespace
    entryTags = entryTags.trim()

    if (entryTags.length > 0) {
      // if there are multiple pairs of square brackets then it's invalid, just use what we started with
      if (entryTags.split('[').length > 2 || entryTags.split(']').length > 2) {
        return entryTags
      }

      // replace any curly quotation marks with straight quotation marks
      entryTags = entryTags.replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, '"')

      // if there are no quotation marks of either sort, add them
      if (entryTags.indexOf('"') < 0 && entryTags.indexOf("'") < 0) {
        // clear square brackets if we have them
        if (entryTags.indexOf('[') === 0 && entryTags.indexOf(']') === entryTags.length - 1) {
          entryTags = entryTags.substring(1, entryTags.length - 1)
        }

        // split based on commas
        let entryTagsSplit = entryTags.split(',')
        let quotedEntryTagsSplit = _.map(entryTagsSplit, tag => {
          return '"' + tag.trim() + '"'
        })
        entryTags = quotedEntryTagsSplit.join(',')
      }

      // if there are no square brackets, add them
      if (entryTags.indexOf('[') < 0 && entryTags.indexOf(']') < 0) {
        entryTags = '[' + entryTags + ']'
      }
    }
  }
  return entryTags
}

const filterByTags = (entries, entryTagsProperty, tags) => {
  try {
    // tags will have single quotes instead of double quotes for JSON string array
    // For example: ['sale', 'special-feature', 'referral']
    // This is mainly to avoid escape characters in these player profile data
    // Therefore, these single quotes should be replaced to double quotes before parsing
    const playerTags = _.map(JSON.parse(tags.replace(/'/g, '"')), tag => {
      // tags are case-insensitive, convert to lowercase
      return tag.toLowerCase()
    })
    return entries.filter(entry => {
      let entryTags = entry ? cleanUpEntryTags(entry[entryTagsProperty]) : null
      if (!entryTags) return true
      try {
        return JSON.parse(entryTags).some(tag => playerTags.includes(tag.toLowerCase()))
      } catch (e) {
        console.warn(`[Utils] Error parsing '${entry.slug}' '${entryTagsProperty}' tags:`, entryTags, e)
        return false
      }
    })
  } catch (e) {
    console.warn(`[Utils] Error parsing '${entryTagsProperty}' tags... Empty or invalid JSON`, e)
    return entries.filter(entry => {
      let entryTags = entry ? cleanUpEntryTags(entry[entryTagsProperty]) : null
      return !entryTags
    })
  }
}

/**
 * @desc Return origin location or origin location + path.
 * @param {string} slug - string path of url
 */
const getLocationOrigin = slug => {
  if (slug && typeof slug === 'string') {
    return window.location.origin + slug
  }

  return window.location.origin
}

/**
 * @desc Return if the feature is enabled for this player ID
 * @param {boolean} Feature enabled flag
 * @param {integer} Feature rollout percentage flag
 * @param {string} Player id
 */
const getFeatureEnabledForPlayer = (featureEnabled, featureEnabledRolloutPercentage, playerId) => {
  if (featureEnabled) {
    if (featureEnabledRolloutPercentage <= 0) {
      return false
    } else if (featureEnabledRolloutPercentage >= 100) {
      return true
    } else if (playerId) {
      let playerIdLength = playerId.length
      let playerIdLastThreeChars = playerIdLength > 3 ? playerId.substring(playerIdLength - 3) : playerId
      let playerIdPercent = parseInt(playerIdLastThreeChars) % 100
      return playerIdPercent < featureEnabledRolloutPercentage
    }
  }
  return false
}

/**
 * @desc Managed enabling and disabling scrolling of the body element for a given key. All keys must be releassed to scroll.
 * @param {boolean} scrollingAllowed
 * @param {string} key
 */
const scrollHoldKeys = [] // key holder

function setBodyCanScrollFor(scrollingAllowed, key) {
  if (scrollingAllowed) {
    if (scrollHoldKeys.includes(key)) {
      // remove element
      scrollHoldKeys.splice(scrollHoldKeys.indexOf(key), 1)
    }

    if (scrollHoldKeys.length <= 0) {
      setBodyCanScroll(true)
    }
  } else {
    if (!scrollHoldKeys.includes(key)) {
      scrollHoldKeys.push(key)
    }
    setBodyCanScroll(false)
  }
}

/**
 * @desc Enables or disables scrolling the body element
 * @param {boolean} scrollingAllowed
 */
function setBodyCanScroll(scrollingAllowed) {
  if (scrollingAllowed) {
    document.body.classList.remove('body-no-scroll')
  } else {
    document.body.classList.add('body-no-scroll')
  }
}

/**
 * @desc Scrolls to subcategory on page.
 * @param {string} name - subcategory name
 * @param {boolean} isSubcategory - is category or subcategory
 */
const scrollToTopOfCategoryName = (name, isSubcategory) => {
  function getOffsetTop(element) {
    return element ? element.offsetTop + getOffsetTop(element.offsetParent) : 0
  }

  const prefixElementId = isSubcategory ? 'subcategory-' : 'category-'
  let category = document.getElementById(prefixElementId + name)
  const navHeight = document.getElementsByTagName('nav')[0].clientHeight
  const navTopPadding = document.getElementById('navbar-top-padding').offsetHeight
  const categoryMenu = document.getElementById('category-menu')
  const subMenu = document.getElementById('sub-category-container')
  const lobby = document.getElementById('game-lobby')
  let diamondsMenu = null
  let isSubcategoryExist = isSubcategory
  const isCategoryInMenu = Boolean(categoryMenu && categoryMenu.contains(category))

  // check if subcategory with category name (in case when isSubcategory = false) really does not exist in DOM
  if (!isCategoryInMenu && !isSubcategory) {
    category = document.getElementById(`subcategory-${name}`)

    if (category) {
      isSubcategoryExist = true
    }
  }

  if (lobby && document.getElementById('diamond-wallet')) {
    diamondsMenu = document.getElementById('diamond-wallet')
  }

  if (!category || !category.style || category.style.display === 'none' || !lobby) {
    return false
  }

  let categoryOffsetTop = getOffsetTop(category)
  let categoryHeight = categoryMenu && categoryMenu.offsetHeight !== 'undefined' ? categoryMenu.offsetHeight : 0
  let subMenuHeight =
    subMenu && subMenu.offsetHeight !== 'undefined'
      ? subMenu.offsetHeight
      : config.defaultCategoryTopMarginWithoutSubcategories
  let diamondsMenuHeight = diamondsMenu && diamondsMenu.offsetHeight !== 'undefined' ? diamondsMenu.offsetHeight : 0

  let scrollValue = 0
  if (!isSubcategory) {
    // if we're using a category, their parent menus don't scroll with the rest of the page...
    // use hacky logic to determine their original (non-scrolled) position, relative to the 'games-section'
    let games = lobby.getElementsByClassName('games-section')[0]
    if (!games || !games.style || games.style.display === 'none' || (!isCategoryInMenu && !isSubcategoryExist)) {
      return false
    }

    const carousel = document.getElementById('carousel-slider')
    const carouselHeight = carousel ? carousel.offsetHeight : 0
    const carouselOffsetTop = carousel ? getOffsetTop(carousel) : navTopPadding + navHeight

    // categoryScrollAdjust exists because I can not account for 2 pixels (possibly drop shadow and rem to px rounding being inconsistent some where)
    scrollValue = carouselHeight + carouselOffsetTop - categoryHeight - navTopPadding - config.categoryScrollAdjust
  } else {
    scrollValue = categoryOffsetTop - navHeight - diamondsMenuHeight - categoryHeight - subMenuHeight - navTopPadding
  }

  window.scroll({ top: scrollValue, behavior: 'smooth' })
  return true
}

/**
 * @desc Scrolls to category on page.
 * @param {string} categoryName - category name
 */
const scrollToCategory = categoryName => {
  let scrolled = scrollToTopOfCategoryName(categoryName, false)
  if (!scrolled) {
    const elementToScrollTo = document.getElementById(`category-${categoryName}`)
    if (elementToScrollTo) {
      elementToScrollTo.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'center'
      })
      return true
    }
    return false
  }
  return true
}

/**
 * @desc Hides the Zendesk chat window. Used to use window.$zopim.livechat.window.hide()
 *  but that stopped working as expected and now must use window.zE('webWidget', ‘close')
 *  instead. Adding a utils method for shared code, just in case they change it again...
 */
const hideChatWindow = () => {
  console.log('[utils] hideChatWindow')

  if (isZendeskWidgetAdded()) {
    closeZendeskWidget()
  }
  if (isCXmessengerAdded()) {
    closeCXMessenger()
  }
  if (isLiveChatWidgetAdded()) {
    closeLiveChatWidget()
  }
}

/**
 * @desc Shows the Zendesk chat window. Added to utils, to coordinate with above hideChatWindow method.
 *  Used to use window.$zopim.livechat.window.show(), which still seems to function hide, but swapped
 *  to coordinating method for consistency.
 */
const showChatWindow = () => {
  console.log('[utils] showChatWindow')

  if (isZendeskWidgetAdded()) {
    openZendeskWidget()
  }
  if (isCXmessengerAdded()) {
    openCXMessenger()
  }
  if (isLiveChatWidgetAdded()) {
    openLiveChatWidget()
  }
}

/**
 * @desc Determines if a tag value is valid for givien help widget
 * @param {Object} widgetConfig config for help widget
 * @param {Object} tagsPlayer string of array with tags
 */
const isHelpWidgetValidForPlayer = (widgetConfig, tagsPlayer) => {
  const playerPropertyValidation = 'tagsPlayer'

  if (tagsPlayer) {
    if (!widgetConfig[playerPropertyValidation]) {
      return true
    } else {
      const widgetObjectArr = cloneDeep(widgetConfig)
      const entries = [widgetObjectArr]
      const filterResult = filterByTags(entries, playerPropertyValidation, tagsPlayer)
      return filterResult.length > 0
    }
  }

  return false
}

/**
 * @desc method set session data with casino mode - lite or default
 * return object that contains init function, previous and current casino mode state
 * e.g. if prev and current not equal it is mean
 * that player moved Casino -> CasinoLite, or CasinoLite -> Casino
 * Note: delete after remove CasinoLite.js
 */
const handleCasinoMode = function(isCasinoLiteMode) {
  const currentCasinoMode = isCasinoLiteMode ? 'lite' : 'default'
  const prevCasinoMode = sessionStorage.getItem('casinoMode') || 'default'

  function initCasinoMode() {
    if (prevCasinoMode !== currentCasinoMode) {
      // hack: this time offset needs because 'casinoMode' override in sessionStorage before reads
      setTimeout(() => {
        sessionStorage.setItem('casinoMode', currentCasinoMode)
      }, config.sessionCasinoModeUpdateDelay)
    }
  }

  return {
    initCasinoMode,
    prevCasinoMode,
    currentCasinoMode
  }
}

/**
 * @desc Return true if player has valid tags for this game
 * @param {boolean}
 * @param {Object} tags an array of strings to compare to the game tags
 * @param {Object} gameObject game object with a gameTags field
 */
function areTagsValidForThisGame(tags, gameObject) {
  if (gameObject) {
    let filterResult = filterByTags([gameObject], 'gameTags', tags)
    return filterResult.length > 0
  } else {
    // treat this game as if it has no tags
    return true
  }
}

/**
 * @des this method check browser tab navigation type for duplication and return boolean
 * see for more https://developer.mozilla.org/en-US/docs/Web/API/PerformanceNavigationTiming/type
 * @returns {boolean} - tab was duplicated or not; default - tab was duplicated
 */
const isBrowserTabDuplicated = () => {
  const navigationPerformance = performance
    .getEntries()
    .filter(entry => entry.entryType === 'navigation' && entry.initiatorType === 'navigation')

  if (navigationPerformance[0] === undefined) {
    return false
  }

  const [PerformanceNavigationTiming] = navigationPerformance

  if (navigator.userAgent.indexOf('Firefox') !== -1 && PerformanceNavigationTiming) {
    return PerformanceNavigationTiming.type === 'back_forward' || PerformanceNavigationTiming.type === 'navigate'
  }

  if (PerformanceNavigationTiming) {
    return PerformanceNavigationTiming.type === 'back_forward'
  }

  return true
}

/**
 * @des this method check player and config accesses for tax id feature
 * @param {string} playerCountry - player country code
 * @returns {boolean} - is for tax id feature allowed or not
 */
const isTaxIdAllowed = playerCountry => {
  return (
    playerCountry &&
    config.supportedTaxIdCountriesCodes.includes(playerCountry.toUpperCase()) &&
    config.enableMyAccountSsnInfo
  )
}

const formatDocumentUploadFilename = filename => {
  return filename && filename.length > config.uploadFilenameCharsLimit + 1
    ? `${filename.slice(0, config.uploadFilenameCharsLimit).trim()}...`
    : filename
}

const storeProductIndex = (playerProductsStore, uuid) => {
  return storeIsInitialized() && !storeIsBlocked(playerProductsStore)
    ? playerProductsStore[playerProductsStoreKeys.productsStoreData][playerProductsStoreKeys.products].findIndex(
        product => product.uuid.value === uuid
      )
    : -1
}

const storeHasProduct = (playerProductsStore, uuid) => {
  return storeProductIndex(playerProductsStore, uuid) !== -1
}

const storeGetProduct = (playerProductsStore, uuid) => {
  if (!uuid) {
    return null
  }

  let index = storeProductIndex(playerProductsStore, uuid)
  if (index === -1) {
    return null
  } else {
    return playerProductsStore[playerProductsStoreKeys.productsStoreData][playerProductsStoreKeys.products][index]
  }
}

const storeIsBlocked = playerProductsStore => {
  return (
    !playerProductsStore ||
    !playerProductsStore[playerProductsStoreKeys.productsStoreData] ||
    playerProductsStore[playerProductsStoreKeys.productsStoreData][playerProductsStoreKeys.blocked]
  )
}

const getCurrencySymbol = currencyCode => {
  if (config.currencySymbolMap && config.currencySymbolMap[currencyCode]) {
    return config.currencySymbolMap[currencyCode]
  }
  return currencyCode
}

const isBannerPurchaseType = banner => {
  return banner.ctaButtonAction && banner.ctaButtonAction.includes('OpenDirectPurchase')
}

const storeIsInitialized = () => {
  if (window.storeInitialized) {
    return true
  }
  return false
}

const replaceTokensWithReplacements = (text, replacements) => {
  let replacedText = text
  if (replacedText.includes('{value')) {
    let values = replacedText.match(/{value1\d}/g)
    _.forEach(values, tag => {
      replacements[tag] = localizedTruncate(replacements[tag], false)
    })
  }
  if (replacedText.includes('{player')) {
    let values = replacedText.match(/{player\d}/g)
    _.forEach(values, tag => {
      replacements[tag] = localizedTruncate(replacements[tag], false)
    })
  }
  replacedText = tokenizer(replacedText, replacements)
  return replacedText
}

const getReplacementsForDirectPurchaseProduct = (replacements, product, prefix = '', popup = false) => {
  if (product) {
    // update strings to match how they display in the store and then pass along for replacements
    for (const property in product) {
      // if we didn't override this property with notification settings...
      if (product[property] !== undefined && product[property] !== null && !replacements['{' + prefix + property + '}'])
        switch (property) {
          // convert basePrice and price to display value (cents as applicable, currency)
          case 'basePrice':
          case 'price':
            replacements['{' + prefix + property + '}'] = getStorePriceDisplayString(
              product[property],
              product['currencyCode']
            )
            break
          // convert "coin" values to cents display if we force two digits
          case 'baseGameCoins':
          case 'baseSweepsCoins':
          case 'gameCoins':
          case 'sweepsCoins':
            replacements['{' + prefix + property + '}'] = (product[property] / 100).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })
            break
          // convert "coin" values without forced fraction digits
          case 'baseCount':
          case 'baseDiamonds':
          case 'count':
          case 'diamonds':
            replacements['{' + prefix + property + '}'] = (product[property] / 100).toLocaleString('en-US')
            break
          // add commas to remaining numbers
          case 'lobbySpins':
          case 'vipPoints':
            replacements['{' + prefix + property + '}'] = product[property].toLocaleString('en-US')
            break
          // extra parameters need their own iterating...
          case 'extraParameters':
            for (const extra in product[property]) {
              // if we didn't override this property with notification settings...
              if (
                product[property][extra] !== undefined &&
                product[property][extra] !== null &&
                !replacements['{' + prefix + property + '.' + extra + '}']
              )
                switch (extra) {
                  case 'extra_daily_sc':
                    replacements['{' + prefix + property + '.' + extra + '}'] = parseFloat(
                      product[property][extra]
                    ).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })
                    break
                  case 'extra_daily_dia':
                    replacements['{' + prefix + property + '.' + extra + '}'] = parseFloat(
                      product[property][extra]
                    ).toLocaleString('en-US')
                    break
                  case 'bonus_duration_in_days':
                    replacements['{' + prefix + property + '.' + extra + '}'] = parseFloat(
                      product[property][extra]
                    ).toLocaleString('en-US')
                    break
                  default:
                    replacements['{' + prefix + property + '.' + extra + '}'] = product[property][extra].toString()
                }
            }
            break
          // for any other properties, just pass along the exact property as a replacement value
          default:
            replacements['{' + prefix + property + '}'] = product[property].toString()
        }
    }

    // if this is a popup, we set a few extra fields...
    if (popup) {
      const empty = '&nbsp;' // we need a blank space in order for formatting to not get messed up when the text is empty

      let showHarvest = false
      let harvestScAmount = null
      let harvestDiaAmount = null
      let harvestDays = null
      if (product.extraParameters) {
        if (product.extraParameters.bonus_duration_in_days) {
          if (product.extraParameters.extra_daily_sc) {
            harvestScAmount = tokenizer(l10n.t['popups.oneOfMulti.harvestScAmount'], {
              '{amount}': replacements['{' + prefix + 'extraParameters.extra_daily_sc}']
            })
          }
          if (product.extraParameters.extra_daily_dia) {
            harvestDiaAmount = tokenizer(l10n.t['popups.oneOfMulti.harvestDiaAmount'], {
              '{amount}': replacements['{' + prefix + 'extraParameters.extra_daily_dia}']
            })
          }
          if (harvestScAmount || harvestDiaAmount) {
            showHarvest = true
            harvestDays = tokenizer(l10n.t['popups.oneOfMulti.harvestDays'], {
              '{days}': replacements['{' + prefix + 'extraParameters.bonus_duration_in_days}']
            })
          }
        }
      }

      const basePrice =
        product.hasOwnProperty('priceSale') && product.priceSale
          ? tokenizer(l10n.t['popups.oneOfMulti.basePrice'], {
              '{basePrice}': replacements['{' + prefix + 'basePrice}']
            })
          : empty
      const scAmount = !!product.sweepsCoins
        ? tokenizer(l10n.t['popups.oneOfMulti.scAmount'], {
            '{amount}': replacements['{' + prefix + 'sweepsCoins}']
          })
        : null
      const diaAmount = !!product.diamonds
        ? tokenizer(l10n.t['popups.oneOfMulti.diaAmount'], {
            '{amount}': replacements['{' + prefix + 'diamonds}']
          })
        : null
      const scAmountWithHarvest = !!product.sweepsCoins
        ? tokenizer(l10n.t['popups.oneOfMulti.scAmountWithHarvest'], {
            '{amount}': replacements['{' + prefix + 'sweepsCoins}']
          })
        : null
      const diaAmountWithHarvest = !!product.diamonds
        ? tokenizer(l10n.t['popups.oneOfMulti.diaAmountWithHarvest'], {
            '{amount}': replacements['{' + prefix + 'diamonds}']
          })
        : null

      replacements['{' + prefix + 'oneOfMultiBasePrice}'] = basePrice
      replacements['{' + prefix + 'oneOfMultiBonusLeft}'] = scAmount ? scAmount : diaAmount ? diaAmount : empty
      replacements['{' + prefix + 'oneOfMultiBonusRight}'] = scAmount && diaAmount ? diaAmount : empty
      replacements['{' + prefix + 'oneOfMultiBonusLeftWithHarvest}'] = scAmountWithHarvest
        ? scAmountWithHarvest
        : diaAmountWithHarvest
        ? diaAmountWithHarvest
        : empty
      replacements['{' + prefix + 'oneOfMultiBonusRightWithHarvest}'] =
        scAmountWithHarvest && diaAmountWithHarvest ? diaAmountWithHarvest : empty
      replacements['{' + prefix + 'oneOfMultiHarvestBonusLeft}'] = showHarvest
        ? harvestDiaAmount
          ? harvestDiaAmount
          : harvestScAmount
        : empty
      replacements['{' + prefix + 'oneOfMultiHarvestBonusRight}'] = showHarvest ? harvestDays : empty
    }
  }
  return replacements
}

const getReplacementsForDirectPurchasePopup = (playerProductsStore, notification) => {
  let replacements = null

  const uuids = getPurchaseNotificationUuids(notification)
  if (uuids) {
    replacements = {}

    for (let i = 0; i < uuids.length; i++) {
      let product = storeGetProduct(playerProductsStore, uuids[i])
      if (product) {
        if (i === 0) {
          // just use the first product for default replacements
          replacements = getReplacementsForDirectPurchaseProduct(replacements, product, '', true)
        }

        // also do replacements per product #, with a prefix:
        // {product1.price}, {product2.price}, etc.
        replacements = getReplacementsForDirectPurchaseProduct(
          replacements,
          product,
          'product' + (i + 1).toString() + '.',
          true
        )
      }
    }
  }

  return replacements
}

/**
 * @des this is used to determine if we are using the sweepstakes webview
 * @param {object} casinoSweepstakesOrigin - sweepstakes origin object
 * @returns {boolean} - true if in sweeps webview
 */
const isSweepsWebView = casinoSweepstakesOrigin => {
  return casinoSweepstakesOrigin && casinoSweepstakesOrigin.page === 'webview'
}

/**
 * @des this is used to determine if we need to force load separate lobby on ios app
 * @param {object} casinoSweepstakesOrigin - sweepstakes origin object
 * @returns {boolean} - true if separate lobby for ios condition is satisfied
 */
const separateLobbyForIOS = casinoSweepstakesOrigin => {
  return config.iOSWebviewLobbySlug && isIOS && isSweepsWebView(casinoSweepstakesOrigin)
}

/**
 * @des Provides top level url to sweepstakes lobby
 * @param {object} casinoSweepstakesOrigin - sweepstakes origin object
 * @param {string} casinoSweepstakesOriginUrl - sweepstakes origin url
 * @returns {string} - to top navigation level link to sweepstakes lobby
 */
const getTopLevelReturnToLobbyURL = (casinoSweepstakesOrigin, casinoSweepstakesOriginUrl) => {
  const isWebview = isSweepsWebView(casinoSweepstakesOrigin)
  const urlPath = isWebview ? (isMobile ? 'gc/reloadSweeps.php' : 'Social/') : 'gc/'
  let lobbyUrl = new URL(`${casinoSweepstakesOriginUrl}/${urlPath}`)

  if (isWebview) {
    if (isMobile) {
      lobbyUrl.searchParams.append('deviceType', isAndroid ? '1' : '2')
    } else {
      // ems social app
      lobbyUrl.searchParams.append('sweepstakes', '1')
    }
  }

  return lobbyUrl.toString()
}

/**
 * @des Provides top level url to sweepstakes store
 * @param {object} casinoSweepstakesOrigin - sweepstakes origin object
 * @param {string} casinoSweepstakesOriginUrl - sweepstakes origin url
 * @returns {string} - to top navigation level link to sweepstakes store
 */
const getTopLevelReturnToStoreURL = (casinoSweepstakesOrigin, casinoSweepstakesOriginUrl) => {
  const isWebview = isSweepsWebView(casinoSweepstakesOrigin)

  let storeUrl = new URL(getTopLevelReturnToLobbyURL(casinoSweepstakesOrigin, casinoSweepstakesOriginUrl))

  if (isWebview && isMobile) {
    storeUrl.searchParams.append('openStore', 'true')
  } else {
    storeUrl.searchParams.append('SweepsCoinStore', '1')
  }

  return storeUrl.toString()
}

const isValueInProviderRegionBlock = (providerConfig, secondaryConfig, value) => {
  let blocked = false
  if (value) {
    if (providerConfig && providerConfig.regionBlock && providerConfig.regionBlock.length > 0) {
      blocked = providerConfig.regionBlock.indexOf(value.toLowerCase()) > -1
    }
    if (!blocked && secondaryConfig && secondaryConfig.regionBlock && secondaryConfig.regionBlock.length > 0) {
      blocked = secondaryConfig.regionBlock.indexOf(value.toLowerCase()) > -1
    }
  }
  return blocked
}

const isValueInProviderAcceptedRegions = (providerConfig, secondaryConfig, value) => {
  let accepted = true
  if (value) {
    if (providerConfig && providerConfig.acceptedRegions && providerConfig.acceptedRegions.length > 0) {
      accepted = providerConfig.acceptedRegions.indexOf(value.toLowerCase()) > -1
    }
    if (!accepted && secondaryConfig && secondaryConfig.acceptedRegions && secondaryConfig.acceptedRegions.length > 0) {
      accepted = secondaryConfig.acceptedRegions.indexOf(value.toLowerCase()) > -1
    }
  }
  return accepted
}

/**
 * @desc Return true if provider is region blocked for player
 * @param {Object} playerData the player object with subdivision field
 * @param {Object} gameObject game object with a provider field
 * @returns {boolean}
 */
const isGameProviderRegionBlocked = (playerData, gameObject) => {
  const providerConfig = !_.isEmpty(gameObject) ? config.providers[gameObject.provider] : null

  if (providerConfig) {
    const secondaryConfig =
      !!gameObject.secondProvider && providerConfig.secondProviders
        ? providerConfig.secondProviders[gameObject.secondProvider]
        : null

    let playerIsInAcceptedRegion =
      isValueInProviderAcceptedRegions(providerConfig, secondaryConfig, playerData.subdivision) ||
      isValueInProviderAcceptedRegions(providerConfig, secondaryConfig, playerData.country)

    if (playerIsInAcceptedRegion) {
      return (
        isValueInProviderRegionBlock(providerConfig, secondaryConfig, playerData.subdivision) ||
        isValueInProviderRegionBlock(providerConfig, secondaryConfig, playerData.country)
      )
    }
    return true
  }

  return false
}

/**
 * @desc Return the boolean value to check if pii data required or not
 * @param {string} playerStatus the player status
 * @returns {boolean}
 */
const forcePiiAndSms = playerStatus => {
  if (config.forceEarlyPiiAndSms) {
    // if we're globally forcing it early then it can't ever be closed
    return true
  } else {
    // if we're not globally forcing it early, then only partially registered accounts are forced
    return playerStatus === config.status.partial
  }
}

/**
 * @desc Optional parameter, for converting markdown links to "_blank" target if external
 *       Common usage: parse(md.render(text), getParseOptionsExternalLinksHaveTargetBlank())
 */
const getParseOptionsExternalLinksHaveTargetBlank = (
  localLinkOnClick = null,
  localLinkOnClickIgnoreRedirect = false
) => {
  return {
    replace: domNode => {
      if (domNode) {
        // handle custom link functionality
        if (domNode.name === 'a' && domNode.attribs && domNode.attribs.href) {
          if (domNode.attribs.href.indexOf('http') === 0 || domNode.attribs.href.indexOf('//') === 0) {
            // if it's an external link, make the target = '_blank'
            domNode.attribs.target = '_blank'
          } else if (localLinkOnClick) {
            // if it's a local link and we have a callback, attach it
            domNode.attribs.onClick = event => {
              if (localLinkOnClickIgnoreRedirect) {
                // if this is true, we'll redirect via code... prevent the default href handling
                event.stopPropagation()
                event.nativeEvent.stopImmediatePropagation()
                event.preventDefault()
              }
              localLinkOnClick(domNode.attribs.href)
            }
          }
        }

        // if it's just a line break and the child of a table, remove it (to prevent validateDOMNesting JS console error)
        if (domNode.data === '\n') {
          let childOfTable = false
          let parent = domNode.parent
          while (parent) {
            if (parent.name === 'table') {
              childOfTable = true
              break
            }
            parent = parent.parent
          }
          if (childOfTable) {
            return <></>
          }
        }
      }
    }
  }
}

/**
 * Return the env name based on the current domain.
 * Note: This is used before the config is loaded, so we can't use config values and have to hardcode it.
 * @returns - the actual env name.
 */
const envLocal = 'local',
  envQA = 'SS-QA',
  envQA2 = 'SS-QA2',
  envStage = 'SS-STAGE',
  envLBVStage = 'LBV-STAGE',
  envProd = 'SS-PROD',
  envLBVProd = 'LBV-PROD'
const getBuildEnvFromDomain = () => {
  switch (window.location.hostname) {
    case 'ss-qa.high5games.com':
      return envQA
    case 'ss-qa2.high5games.com':
      return envQA2
    case 'ss-stage.high5casino.com':
      return envStage
    case 'ss-stage2.high5casino.com':
      return envLBVStage
    case 'sweeps.high5casino.com':
      return envProd
    case 'lobby.luckybitsvegas.com':
      return envLBVProd
    case 'localhost':
      return envLocal
    default:
      if (window.location.port !== '') {
        // localhost will use a port (might hit this if using IP rather than 'localhost')
        return envLocal
      } else {
        // when in doubt, assume prod (not sure how we'd get here, but just in case)
        return envProd
      }
  }
}

/**
 * Return Sentry traces sample rate per env.
 * Note: This is used before the config is loaded, so we can't use config values.
 * @returns {number} - number between 0 and 1 (1 means 100% of transactions will be sent to Sentry)
 */
const getSentryTracesSampleRate = () => {
  const DEFAULT_TRACES_SAMPLE_RATE = 0.01
  switch (getBuildEnvFromDomain()) {
    case envLocal:
    case envQA:
    case envQA2:
    case envStage:
    case envLBVStage:
      return 1
    case envProd:
    case envLBVProd:
    default:
      return DEFAULT_TRACES_SAMPLE_RATE
  }
}

/**
 * Return Sentry profiles sample rate per env.
 * Note: This is used before the config is loaded, so we can't use config values.
 * @returns {number} - number between 0 and 1 (1 means 100% of transactions will be sent to Sentry)
 */
const getSentryProfilesSampleRate = () => {
  const DEFAULT_PROFILES_SAMPLE_RATE = 0.01
  switch (getBuildEnvFromDomain()) {
    case envLocal:
    case envQA:
    case envQA2:
    case envStage:
    case envLBVStage:
      return 1
    case envProd:
    case envLBVProd:
    default:
      return DEFAULT_PROFILES_SAMPLE_RATE
  }
}

/**
 * Retrieves the cryptocurrency configuration for a given currency ID.
 *
 * @param {string} currencyId - The ID of the cryptocurrency.
 * @returns {Object|undefined} - The configuration object for the cryptocurrency, or undefined if not found.
 */
const getCryptoCurrencyConfigForCryptoId = currencyId => {
  return _.find(config.cashierCurrencyDisplay, { currencyId })
}

/**
 * @desc Function returns currency data, based on currency id
 * @param {string} currencyId the currency id
 * An object with at least two props: currencyId, currencyName.
 * Pulled from config.currencyDisplay data.
 * @typedef {Object} CurrencyData
 * @property {string} currencyId - the currency id
 * @property {string} currencyName - the currency display name
 * @return {CurrencyData}
 */
const getCurrencyConfigForCurrencyId = currencyId => {
  return _.find(config.currencyDisplay, { currencyId })
}

/**
 * @desc Function returns currency data, based on wallet id
 * @param {Object} playerWallets player wallets data
 * @param {string} walletId the player wallet id
 * An object with at least two props: currencyId, currencyName.
 * Pulled from config.currencyDisplay data.
 * @typedef {Object} CurrencyData
 * @property {string} currencyId - the currency id
 * @property {string} currencyName - the currency display name
 * @return {CurrencyData}
 */
const getCurrencyConfigForWalletId = (playerWallets, walletId) => {
  const currencyId = _.isEmpty(playerWallets)
    ? getCurrencyByWalletId(walletId)
    : playerWallets[walletId] && playerWallets[walletId].currencyId

  if (currencyId) {
    return getCurrencyConfigForCurrencyId(currencyId)
  } else {
    return null
  }
}

const getWalletIdByCurrency = currency => {
  let walletId
  switch (currency) {
    case 'DIA':
      walletId = config.diamondWalletId
      break
    case 'GC':
      walletId = config.gcWalletId
      break
    case 'SWC':
    case 'SC':
      walletId = config.swcWalletId
      break
    case 'SWT':
      walletId = config.swtWalletId
      break
    default:
      break
  }
  return walletId
}

const getCurrencyByWalletId = walletId => {
  let currencyId
  switch (walletId) {
    case config.diamondWalletId:
      currencyId = 'DIA'
      break
    case config.gcWalletId:
      currencyId = 'GC'
      break
    case config.swcWalletId:
      currencyId = 'SWC'
      break
    case config.swtWalletId:
      currencyId = 'SWT'
      break
    default:
      break
  }
  return currencyId
}

const getActiveCurrencyName = (playerWallets, playerActiveCurrencyId) => {
  const currencyConfig = getCurrencyConfigForWalletId(playerWallets, playerActiveCurrencyId)
  return currencyConfig ? currencyConfig.currencyName : ''
}

/**
 * @desc Get formatted date
 * @param {string} dateNumber - player last login date
 * @param {boolean} useDefaultFormat
 * @returns {string}
 */
const getFormattedDateString = (dateNumber, useDefaultFormat) => {
  // If useDefaultFormat, return date in "January 1, 2024 at 11:59 PM" format, else return date in "1/1/2024 @ 11:59:00 PM" format
  if (useDefaultFormat) {
    let date = moment(dateNumber)
    return date.format(l10n.t['lobby.dateFormat'])
  } else {
    let date = new Date(dateNumber)
    return `${date.toLocaleDateString()} @ ${date.toLocaleTimeString()}`
  }
}

/**
 * @desc Get formatted datetime
 * @param {string} format - datetime format, example is "{hh}h{mm}m{ss}s"
 * @param {number} days - days amount
 * @param {number} hours - hours amount
 * @param {number} minutes - minutes amount
 * @param {number} seconds - seconds amount
 * @returns {string}
 */
const getFormattedDatetime = (format, days, hours, minutes, seconds) => {
  const twoCharsMinimumFormat = value => {
    if (value.toString().length === 1) {
      return `0${value}`
    } else {
      return `${value}`
    }
  }

  return tokenizer(format, {
    '{days}': days,
    '{hh}': twoCharsMinimumFormat(hours),
    '{mm}': twoCharsMinimumFormat(minutes),
    '{ss}': twoCharsMinimumFormat(seconds)
  })
}

/**
 * @desc Return true if provider value is numeric type
 * @param {string | number} value value to check
 * @returns {boolean}
 */
const isNumeric = value =>
  (typeof value === 'number' || (typeof value === 'string' && value.trim() !== '')) && !isNaN(value)

const getLeaderBoardRowClass = (rank, id, isSpacer, isEmptyUser, userId, isTable) => {
  if (isSpacer) {
    return `spacer`
  } else if (isEmptyUser) {
    return `empty`
  } else if (id.toString() === userId) {
    return `selected${isTable ? '-table' : ''}`
  } else if (rank === 1) {
    return 'first'
  } else if (rank === 2) {
    return 'second'
  } else if (rank === 3) {
    return 'third'
  } else {
    return ''
  }
}

/**
 * @name setPrivacySettings
 *
 * @param {string} id - The privacy setting that is being adjusted.
 * @param {string} checked - The state of the privacy setting (true/false).
 * @param {object} playerData - player data object.
 *
 * @desc A function that sets a user's privacy settings
 */
const setSinglePrivacySetting = (id, checked, playerData) => {
  if (playerData.status === config.status.registered || playerData.status === config.status.partial) {
    const getPrivacySettingsUrl =
      config.apiUrl + config.getPrivacySettings.replace('{userId}', playerData.loginUser.userId)

    fetch(getPrivacySettingsUrl, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${playerData.accessToken.encryptedToken}`
      },
      body: JSON.stringify({
        [id]: checked
      })
    }).then(response => {
      if (response.status === HttpStatus.OK) {
        response.json().then(() => {})
      } else {
        response.json().then(err => {
          console.log('[Utils Privacy Settings - PUT] - 400s Bad Request?', err.errorReason)
        })
      }
    })
  }
}

/**
 * @name getCleanedUpVideoUrl
 *
 * @param {string} url - The video URL to adjust
 *
 * @returns {string} - The adjusted video URL
 */
const getCleanedUpVideoUrl = url => {
  if (url) {
    if (url.indexOf('rel=0') > 0) {
      return url
    } else if (url.indexOf('?') > 0) {
      return url + '&rel=0'
    } else {
      return url + '?rel=0'
    }
  } else {
    return ''
  }
}

/**
 * @name convertRemToPx
 *
 * @param {string} remSize - Size in rem
 * @param {number} pixelsPerRem - (Optional) Integer of pixels per rem.
 *
 * @desc A function that converts rem size to pixel size
 */
const convertRemToPx = (remSize, pixelsPerRem = config.pixelsPerRem) => {
  return parseFloat(_.trimEnd(remSize, 'rem')) * pixelsPerRem
}

/**
 * Dynamically imports a component and handles retry logic with page reloads.
 *
 * This function attempts to import a component using a provided dynamic import function.
 * If the import fails and the page has not been refreshed yet, it reloads the page to
 * retry the import. The function tracks whether the page has been refreshed using
 * sessionStorage to avoid infinite reloads. After a successful import or if a retry fails,
 * the session storage flag is reset.
 *
 * @function lazyRetry
 * @param {Function} componentImport - A function that dynamically imports the component.
 * @param {string} componentName - A unique identifier for the component being imported.
 *                                 Used as part of the sessionStorage key to track retries.
 * @returns {Promise<any>} A promise that resolves with the imported component or rejects
 *                         with an error if the import fails after a retry.
 */

const lazyRetry = function(componentImport, componentName) {
  return new Promise((resolve, reject) => {
    const sessionStorageKeyName = `retry-${componentName}-refreshed`
    const hasRefreshed = JSON.parse(window.sessionStorage.getItem(sessionStorageKeyName) || 'false')

    componentImport()
      .then(component => {
        window.sessionStorage.setItem(sessionStorageKeyName, 'false')
        resolve(component)
      })
      .catch(error => {
        if (!hasRefreshed) {
          window.sessionStorage.setItem(sessionStorageKeyName, 'true')
          return window.location.reload()
        }
        reject(error)
      })
  })
}

/**
 * Retrieves the major version of the OS from the `osVersion` string.
 *
 * This function extracts the first segment of the `osVersion` string (delimited by `.`)
 * and attempts to parse it as a number. If the parsing fails (resulting in `NaN`), it logs
 * an error message and returns `false`.
 *
 * @function
 * @returns {number|boolean} The major version of the OS as a number, or `false` if the version is invalid or not detected.
 */
const getOsVersion = function() {
  const [iOsVersion] = osVersion.split('.')
  let output = isNaN(Number(iOsVersion)) || Number(iOsVersion)

  // this means that output equal to true because it is NaN
  if (typeof output === 'boolean') {
    console.log('[getOsVersion] - os version not detected')
    output = false
  }

  return output
}

/**
 * @desc Closes Drawers only after all modals are closed to prevent scrolling issues
 */
const closeAllDrawersAfterModalsClosed = closeAllDrawer => {
  const runTimeout = currentAttemptsCount => {
    const attempts = currentAttemptsCount

    setTimeout(() => {
      const modal = document.getElementsByClassName('modal')[0]
      if (!modal) {
        if (closeAllDrawer) {
          closeAllDrawer()
        }
      } else if (attempts > 1) {
        runTimeout(attempts - 1)
      }
    }, config.beforeCloseDrawersModalHidingDelayStepMs)
  }

  setTimeout(() => {
    runTimeout(config.beforeCloseDrawersModalHidingAttempts)
  }, config.beforeCloseDrawersModalHidingDelayStartMs)
}

/**
 * Determines the initial language setting based on the browser's language or the URL path.
 *
 * @returns {string} The ISO 639-1 language code (e.g., 'en', 'fr') determined from the user's environment.
 *
 * This function attempts to determine the language as follows:
 * 1. Defaults to 'en' if `window` or `navigator` is not available.
 * 2. Uses the first two characters of `navigator.language` to infer the language.
 * 3. Checks the first 5 characters of the URL path (e.g., '/en-us') for a valid
 *    language-region format (e.g., 'en-us', 'fr-ca') and overrides the inferred language
 *    if found.
 */
const getInitLanguage = () => {
  let isoLang = 'en' // default to 'en' if window or navigator is not available

  if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
    const candidate = window.location.pathname.substring(1, 6)
    isoLang = navigator.language.substr(0, 2)

    // check url to set country
    if (candidate.match(/[a-z]{2}-[a-z]{2,3}/)) {
      isoLang = candidate.substr(0, 2)
    }
  }

  return isoLang
}

/**
 * Capitalizes the first character of a string.
 *
 * @param {string} s - The input string to be capitalized.
 * @returns {string} A new string with the first character capitalized, or an empty string if the input is falsy.
 */
const capitalizeString = s => (s && typeof s === 'string' ? String(s[0]).toUpperCase() + String(s).slice(1) : '')

/**
 * Returns the smallest or largest number from a given set of numbers.
 *
 * @param {'min' | 'max'} mode - Determines whether to return the smallest ("min") or largest ("max") value.
 * @param {...number} numbers - A list of numbers to evaluate.
 * @returns {number} The smallest or largest number based on the mode.
 *
 * @example
 * getMinMaxValue("min", 10, 5, 8, 20); // Returns 5
 * getMinMaxValue("max", 10, 5, 8, 20);  // Returns 20
 */
function getMinMaxValue(mode, ...numbers) {
  return mode === 'min' ? Math.min(...numbers) : Math.max(...numbers)
}

/**
 * Return theme icon.
 *
 * @param {string} relativeImagePath - Icon name.
 * @returns {string} - path to icon.
 */
function getThemeImageUrl(relativeImagePath) {
  return `${process.env.PUBLIC_URL}/${process.env.REACT_APP_CASINO_IMAGE_PATH}/${relativeImagePath}`
}

const sweepsGeoStatusIsBlocked = () => {
  const zustandPlayerStore = readZustandLocalStorage(playerStoreKey)
  return zustandPlayerStore.geoblockLobbyInteraction
}

export {
  tokenizer,
  convertToDollar,
  localizedTruncate,
  equalsFirstKey,
  reUpAuthToken,
  sliceTitle,
  humanReadableTime,
  convertUrlToWebp,
  convertUrlToJpg,
  getImageMimeType,
  getAllowDemoGames,
  getShowPlayButtons,
  getUseRoundPlayButton,
  getWallet,
  getPageTitle,
  getPageDesc,
  getFeatureByLocale,
  getFeatureByLocaleMerged,
  getTopLevelReturnToLobbyURL,
  getTopLevelReturnToStoreURL,
  playerPermissions,
  elementSupportsAttribute,
  makeCMSSiteName,
  formatCurrencyAmount,
  formatCurrencyAmountFromConfig,
  getRedeemablePlayerWallets,
  toCents,
  isImgLoadingAttributeSupported,
  verifyNotFrozen,
  isStatusNotification,
  isNotificationLimit,
  isNotificationExpired,
  isNotificationTimePending,
  getTimezoneOffsetString,
  getTimezoneOffsetStringFromSocialData,
  convertNextTriggerTimeToMs,
  getPathToGame,
  isInGame,
  isInLobby,
  gameIsDemoEnabled,
  demoGamesAvailable,
  isPlayerTestAccount,
  isGameRestrictedForTesting,
  getGameObj,
  canBypassMaintenance,
  cleanUpEntryTags,
  filterByTags,
  getLocationOrigin,
  getFeatureEnabledForPlayer,
  setBodyCanScrollFor,
  scrollToTopOfCategoryName,
  hideChatWindow,
  showChatWindow,
  isHelpWidgetValidForPlayer,
  handleCasinoMode,
  areTagsValidForThisGame,
  isBrowserTabDuplicated,
  isTaxIdAllowed,
  storeHasProduct,
  storeGetProduct,
  storeIsBlocked,
  getCurrencySymbol,
  isNotificationPurchaseType,
  getPurchaseNotificationUuids,
  arePurchaseNotificationProductsAvailable,
  getPurchaseNotificationUuidForCtaNumber,
  getNotificationCtaForCtaNumber,
  isBannerPurchaseType,
  isSweepsWebView,
  separateLobbyForIOS,
  storeIsInitialized,
  replaceTokensWithReplacements,
  getReplacementsForDirectPurchaseProduct,
  getReplacementsForDirectPurchasePopup,
  scrollToCategory,
  isCustomCategory,
  isGameProviderRegionBlocked,
  forcePiiAndSms,
  getCurrencyConfigForCurrencyId,
  getCurrencyConfigForWalletId,
  getWalletIdByCurrency,
  getActiveCurrencyName,
  getFormattedDateString,
  getFormattedDatetime,
  isNumeric,
  getParseOptionsExternalLinksHaveTargetBlank,
  getBuildEnvFromDomain,
  getSentryTracesSampleRate,
  getSentryProfilesSampleRate,
  getLeaderBoardRowClass,
  setSinglePrivacySetting,
  getCurrencyByWalletId,
  getCleanedUpVideoUrl,
  convertRemToPx,
  formatDocumentUploadFilename,
  lazyRetry,
  getInitLanguage,
  capitalizeString,
  getOsVersion,
  closeAllDrawersAfterModalsClosed,
  getMinMaxValue,
  getThemeImageUrl,
  getCryptoCurrencyConfigForCryptoId,
  sweepsGeoStatusIsBlocked
}
